import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

type ThemeCtx = {
  theme: Theme;
  toggle: () => void;
  setTheme: (next: Theme) => void;
};

const STORAGE_KEY = "zworks-theme";

function resolveInitial(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function apply(next: Theme) {
  document.documentElement.classList.toggle("dark", next === "dark");
}

// ponytail: inline provider (no React.memo / no context wrapper). App owns the
// single source of truth; useTheme subscribes via useState in App.tsx.
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => resolveInitial());

  useEffect(() => {
    apply(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* private mode / quota — silently ignore */
    }
  }, [theme]);

  return {
    theme,
    setTheme: (next: Theme) => setThemeState(next),
    toggle: () => setThemeState((current) => (current === "dark" ? "light" : "dark")),
  } satisfies ThemeCtx;
}

// ThemeContext kept for legacy imports — no provider is mounted; consumers
// receive the default value above. Safe to delete once no callers remain.
const ThemeContext = createContext<ThemeCtx>({ theme: "light", toggle: () => {}, setTheme: () => {} });
export type { Theme };
